/**
 * Where the chat view lives (Speedies, F-129). With `speedies` on, chat is its own page at /chat;
 * with it off, it's the Chats tab of /community, as before. Every browser link into chat goes
 * through here, and the /community route redirects old `tab=chats` links (and links that carry a
 * conversation or message with no tab), so stored notification URLs keep working.
 */
import { isSpeediesEnabled } from '@/lib/feature-flags';

export type ChatFilter = 'all' | 'groups' | 'external' | 'dms';

/** The search keys the chat view reads. The /chat route accepts exactly these. */
export interface ChatSearch {
  filter?: ChatFilter;
  community?: string;
  conversation?: string;
  message?: string;
}

/** The chat keys of any search object, with unset keys dropped. */
export function chatSearchFrom(s: Partial<Record<keyof ChatSearch, unknown>>): ChatSearch {
  const out: ChatSearch = {};
  if (typeof s.filter === 'string') out.filter = s.filter as ChatFilter;
  if (typeof s.community === 'string') out.community = s.community;
  if (typeof s.conversation === 'string') out.conversation = s.conversation;
  if (typeof s.message === 'string') out.message = s.message;
  return out;
}

export type ChatLink =
  | { to: '/chat'; search: ChatSearch }
  | { to: '/community'; search: ChatSearch & { tab: 'chats' } };

/** A `navigate()` / `<Link>` target for the chat view, carrying every chat key given. */
export function chatLink(s: ChatSearch = {}, speediesOn = isSpeediesEnabled()): ChatLink {
  const search = chatSearchFrom(s);
  return speediesOn ? { to: '/chat', search } : { to: '/community', search: { ...search, tab: 'chats' } };
}

/** The same as a URL string, for code that stores or builds hrefs. */
export function chatHref(s: ChatSearch = {}, speediesOn = isSpeediesEnabled()): string {
  const link = chatLink(s, speediesOn);
  // `tab` first, so the flag-off URL is exactly today's (`/community?tab=chats&filter=dms`).
  const { tab, ...rest } = link.search as ChatSearch & { tab?: string };
  const entries = [
    ...(tab ? [['tab', tab]] : []),
    ...Object.entries(rest).filter(([, v]) => typeof v === 'string'),
  ] as [string, string][];
  const q = new URLSearchParams(entries).toString();
  return q ? `${link.to}?${q}` : link.to;
}
